import { desiOf, detectSymbology, itf14FromGtin13, type BarcodeSymbology, type ProductDetail, type ProductInput, type ProductStatus } from '@wms/domain';
import { useEffect, useState } from 'react';
import { api, errorDetails, errorMessage } from '../../api';
import { decimalInput, fmt } from '../../format';
import { navigate } from '../../router';
import { StockCard } from '../Stock';
import { ProductCostCard } from './ProductCost';
import { ErrorBanner, SellerChip, Spinner, useLoad, useToast } from '../../ui';
import { Icon } from '../../ui/icons';
import { codeChoiceText, labelCode, labelCodeChoices, type LabelCodeChoice } from '../../productLabels';
import { ProductLabelDialog } from './ProductLabel';
import { SellerSelect, useSellerPick } from './SellerPick';

const SYMBOLOGY: Record<BarcodeSymbology, string> = {
  ean13: 'EAN-13',
  ean8: 'EAN-8',
  upc_a: 'UPC-A',
  itf14: 'ITF-14',
  gs1_128: 'GS1-128',
  code128: 'Kendi kodu',
  qr: 'QR',
  other: 'Diğer',
};

interface PackForm {
  readonly on: boolean;
  readonly units: string;
  readonly length: string;
  readonly width: string;
  readonly height: string;
  readonly weight: string;
  readonly barcode: string;
}

interface Form {
  readonly sku: string;
  readonly name: string;
  readonly brand: string;
  readonly category: string;
  readonly imageUrl: string;
  readonly description: string;
  readonly length: string;
  readonly width: string;
  readonly height: string;
  readonly weight: string;
  readonly barcodes: readonly { readonly value: string; readonly primary: boolean }[];
  readonly inner: PackForm;
  readonly kase: PackForm;
  readonly trackLot: boolean;
  readonly trackExpiry: boolean;
  readonly minShelfLifeDays: string;
  readonly isFragile: boolean;
  readonly unitLabels: boolean;
  readonly shipsAsIs: boolean;
  readonly labelBeforePick: boolean;
  readonly status: ProductStatus;
}

const NO_PACK: PackForm = { on: false, units: '', length: '', width: '', height: '', weight: '', barcode: '' };
const EMPTY: Form = {
  sku: '',
  name: '',
  brand: '',
  category: '',
  imageUrl: '',
  description: '',
  length: '',
  width: '',
  height: '',
  weight: '',
  barcodes: [{ value: '', primary: true }],
  inner: NO_PACK,
  kase: NO_PACK,
  trackLot: false,
  trackExpiry: false,
  minShelfLifeDays: '',
  isFragile: false,
  unitLabels: true,
  shipsAsIs: false,
  labelBeforePick: false,
  status: 'active',
};

const cm = (mm: number | null) => (mm === null ? '' : String(mm / 10).replace('.', ','));
const kg = (g: number | null) => (g === null ? '' : String(g / 1000).replace('.', ','));
const toMm = (v: string) => (decimalInput(v, 1) === null ? null : Math.round(Number(decimalInput(v, 1)) * 10));
const toG = (v: string) => (decimalInput(v, 3) === null ? null : Math.round(Number(decimalInput(v, 3)) * 1000));
const cleanCode = (v: string) => v.replace(/\s+/g, '').toUpperCase();

function fromDetail(p: ProductDetail): Form {
  const pack = (level: 'inner' | 'case'): PackForm => {
    const k = p.packagings.find((x) => x.level === level);
    return k
      ? { on: true, units: String(k.unitsPerPack), length: cm(k.lengthMm), width: cm(k.widthMm), height: cm(k.heightMm), weight: kg(k.grossWeightG), barcode: k.barcode ?? '' }
      : NO_PACK;
  };
  const unitCodes = p.barcodes.filter((b) => b.packagingLevel === 'each');
  return {
    sku: p.sku,
    name: p.name,
    brand: p.brand ?? '',
    category: p.category ?? '',
    imageUrl: p.imageUrl ?? '',
    description: p.description ?? '',
    length: cm(p.lengthMm),
    width: cm(p.widthMm),
    height: cm(p.heightMm),
    weight: kg(p.grossWeightG),
    barcodes: unitCodes.length > 0 ? unitCodes.map((b) => ({ value: b.barcode, primary: b.isPrimary })) : [{ value: '', primary: true }],
    inner: pack('inner'),
    kase: pack('case'),
    trackLot: p.trackLot,
    trackExpiry: p.trackExpiry,
    minShelfLifeDays: p.minShelfLifeDays === null ? '' : String(p.minShelfLifeDays),
    isFragile: p.isFragile,
    unitLabels: p.unitLabels,
    shipsAsIs: p.shipsAsIs,
    labelBeforePick: p.labelBeforePick,
    status: p.status,
  };
}

function toInput(f: Form): ProductInput {
  const pack = (level: 'inner' | 'case', k: PackForm) =>
    k.on
      ? [
          {
            level,
            unitsPerPack: Number(k.units) || 0,
            lengthMm: toMm(k.length) ?? 0,
            widthMm: toMm(k.width) ?? 0,
            heightMm: toMm(k.height) ?? 0,
            grossWeightG: toG(k.weight) ?? 0,
            barcode: cleanCode(k.barcode) || null,
          },
        ]
      : [];
  return {
    sku: f.sku.trim(),
    name: f.name.trim(),
    brand: f.brand.trim() || null,
    category: f.category.trim() || null,
    imageUrl: f.imageUrl.trim() || null,
    description: f.description.trim() || null,
    lengthMm: toMm(f.length),
    widthMm: toMm(f.width),
    heightMm: toMm(f.height),
    grossWeightG: toG(f.weight),
    trackLot: f.trackLot,
    trackExpiry: f.trackExpiry,
    minShelfLifeDays: f.trackExpiry && f.minShelfLifeDays.trim() ? Number(f.minShelfLifeDays) : null,
    isFragile: f.isFragile,
    unitLabels: f.unitLabels,
    shipsAsIs: f.shipsAsIs,
    labelBeforePick: f.shipsAsIs && f.labelBeforePick,
    status: f.status,
    barcodes: f.barcodes.filter((b) => b.value.trim()).map((b) => ({ barcode: cleanCode(b.value), isPrimary: b.primary })),
    packagings: [...pack('inner', f.inner), ...pack('case', f.kase)],
  };
}

function CodeChip({ value }: { value: string }) {
  const code = cleanCode(value);
  if (!code) return null;
  const symbology = detectSymbology(code);
  return symbology ? <span className="chip ok">{SYMBOLOGY[symbology]}</span> : <span className="chip bad">Hatalı kod</span>;
}

function desiText(length: string, width: string, height: string): string | null {
  const [l, w, h] = [toMm(length), toMm(width), toMm(height)];
  return l && w && h ? fmt.dec(desiOf({ lengthMm: l, widthMm: w, heightMm: h })) : null;
}

/** "Barkodu bas" next to a saved barcode (whoever may add one may print it). */
function PrintCode({ onPrint }: { onPrint: () => void }) {
  return (
    <button type="button" className="btn sm" onClick={onPrint} title="Bu barkodun etiketini basar: bu bilgisayarın ya da bir paketleme masasının yazıcısından">
      <Icon name="printer" size={16} /> Barkodu bas
    </button>
  );
}

function PackFields({
  label,
  value,
  onChange,
  onItf,
  onPrint,
}: {
  label: string;
  value: PackForm;
  onChange: (next: PackForm) => void;
  onItf?: (() => string | null) | undefined;
  /** the barcode as saved: printed from here */
  onPrint?: (() => void) | undefined;
}) {
  const set = (key: keyof PackForm) => (e: { target: { value: string } }) => onChange({ ...value, [key]: e.target.value });
  const desi = desiText(value.length, value.width, value.height);
  return (
    <div className="price-card">
      <label className="row" style={{ gap: 8 }}>
        <input type="checkbox" checked={value.on} onChange={(e) => onChange({ ...value, on: e.target.checked })} />
        <b>{label}</b>
        {value.on && desi && <span className="muted">· {desi} desi</span>}
      </label>
      {value.on && (
        <div className="form-grid">
          <div className="field">
            <label>İçindeki adet</label>
            <input className="input" inputMode="numeric" value={value.units} onChange={set('units')} />
          </div>
          <div className="field">
            <label>En (cm)</label>
            <input className="input" inputMode="decimal" value={value.length} onChange={set('length')} />
          </div>
          <div className="field">
            <label>Boy (cm)</label>
            <input className="input" inputMode="decimal" value={value.width} onChange={set('width')} />
          </div>
          <div className="field">
            <label>Yükseklik (cm)</label>
            <input className="input" inputMode="decimal" value={value.height} onChange={set('height')} />
          </div>
          <div className="field">
            <label>Brüt ağırlık (kg)</label>
            <input className="input" inputMode="decimal" value={value.weight} onChange={set('weight')} />
          </div>
          <div className="field">
            <label>Barkod (isteğe bağlı)</label>
            <div className="row" style={{ gap: 6, flexWrap: 'nowrap' }}>
              <input className="input mono" value={value.barcode} onChange={set('barcode')} />
              <CodeChip value={value.barcode} />
              {onPrint && <PrintCode onPrint={onPrint} />}
            </div>
            {onItf && !value.barcode && (
              <button
                type="button"
                className="btn sm ghost"
                onClick={() => {
                  const code = onItf();
                  if (code) onChange({ ...value, barcode: code });
                }}
              >
                Ürün EAN'ından ITF-14 üret
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/** A product as the seller (or the warehouse for the seller) defines it; /urunler/yeni or /urunler/:id. */
export function ProductForm({ id }: { id: string | null }) {
  const toast = useToast();
  const pick = useSellerPick();
  const existing = useLoad(() => (id ? api<ProductDetail>('GET', `/api/products/${id}`) : Promise.resolve(undefined)), [id]);
  const [form, setForm] = useState<Form | null>(id ? null : EMPTY);
  const [problem, setProblem] = useState<{ message: string; details: string[] } | null>(null);
  const [busy, setBusy] = useState(false);
  // "Etiket bas" (the main barcode) or "Barkodu bas" (that barcode): the code the dialog starts with
  const [labeling, setLabeling] = useState<{ readonly code: string | null } | null>(null);
  // barcodes the last save added: offered for printing at once
  const [fresh, setFresh] = useState<readonly LabelCodeChoice[]>([]);
  useEffect(() => {
    if (existing.data) setForm(fromDetail(existing.data));
  }, [existing.data]);
  // another product opened: what this one's save added is not offered there
  useEffect(() => setFresh([]), [id]);

  if (existing.error) return <ErrorBanner message={existing.error} onRetry={() => void existing.reload()} />;
  if (!form) return <Spinner />;
  const f = form;
  const set = (key: keyof Form) => (e: { target: { value: string } }) => setForm({ ...f, [key]: e.target.value });
  const desi = desiText(f.length, f.width, f.height);
  const primaryEan = f.barcodes.map((b) => cleanCode(b.value)).find((c) => detectSymbology(c) === 'ean13') ?? null;
  const readOnly = !pick.canEdit;
  // labels print the saved primary barcode; a product without one gets an in-house EAN-13
  const saved = existing.data;
  const savedCode = saved?.barcodes.find((b) => b.packagingLevel === 'each' && b.isPrimary)?.barcode ?? saved?.barcodes.find((b) => b.packagingLevel === 'each')?.barcode ?? null;
  const savedCodes = new Set(saved?.barcodes.map((b) => b.barcode) ?? []);
  // a row's code once it is saved as it reads now: it can be printed
  const printable = (value: string) => (savedCodes.has(cleanCode(value)) ? () => setLabeling({ code: cleanCode(value) }) : undefined);

  const internalBarcode = async () => {
    if (!id) return;
    setBusy(true);
    try {
      const updated = await api<ProductDetail>('POST', `/api/products/${id}/internal-barcode`);
      const before = new Set(saved?.barcodes.map((b) => b.barcode) ?? []);
      // made to be printed: offered at once
      setFresh(labelCodeChoices(updated.barcodes.filter((b) => !before.has(b.barcode)), null));
      setForm(fromDetail(updated));
      void existing.reload();
      toast(`İç barkod verildi: ${updated.barcodes.find((b) => b.isPrimary)?.barcode ?? ''}`, 'ok');
    } catch (e) {
      toast(errorMessage(e), 'bad');
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const body = toInput(f);
      const saved = id
        ? await api<ProductDetail>('PUT', `/api/products/${id}`, body)
        : await api<ProductDetail>('POST', '/api/products', { ...body, ...(pick.staff && pick.tenantId ? { tenantId: pick.tenantId } : {}) });
      toast(id ? 'Ürün kaydedildi' : `${saved.sku} eklendi`, 'ok');
      if (id) {
        const before = new Set(existing.data?.barcodes.map((b) => b.barcode) ?? []);
        setFresh(labelCodeChoices(saved.barcodes.filter((b) => !before.has(b.barcode)), null));
        setForm(fromDetail(saved));
        void existing.reload();
      } else navigate(`/urunler/${saved.id}`);
    } catch (e) {
      const details = errorDetails(e);
      setProblem({ message: errorMessage(e), details: details.length > 1 ? details : [] });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="row">
        <a className="btn ghost" href="#/urunler">
          <Icon name="back" /> {pick.staff ? 'Ürünler' : 'Ürünler ve stok'}
        </a>
        <h2 style={{ margin: 0 }}>{id ? f.name || f.sku : 'Yeni ürün'}</h2>
        {existing.data && <SellerChip code={existing.data.tenantCode} />}
        {!id && <SellerSelect pick={pick} />}
        {/* outside the form's fieldset: anyone who sees the product may print its label */}
        {saved && labelCode({ barcode: savedCode, sku: saved.sku }) && (
          <button type="button" className="btn" style={{ marginLeft: 'auto' }} onClick={() => setLabeling({ code: savedCode })}>
            <Icon name="printer" size={18} /> Etiket bas
          </button>
        )}
      </div>
      {labeling && saved && (
        <ProductLabelDialog
          title={labeling.code && labeling.code !== savedCode ? `Barkodu bas · ${labeling.code}` : undefined}
          products={[
            {
              id: saved.id,
              sku: saved.sku,
              name: saved.name,
              barcode: labeling.code,
              codes: labelCodeChoices(saved.barcodes, saved.sku),
              sellerCode: saved.tenantCode,
              trackLot: saved.trackLot,
              trackExpiry: saved.trackExpiry,
              unitLabels: saved.unitLabels,
              physical: saved.physical,
            },
          ]}
          onClose={() => setLabeling(null)}
        />
      )}
      {problem && (
        <div className="banner bad">
          <div>
            {problem.message}
            {problem.details.length > 0 && <div style={{ fontWeight: 500 }}>{problem.details.join(' · ')}</div>}
          </div>
        </div>
      )}
      {/* a seller's Ürünler ve stok: the product's stock and lots above what defines it */}
      {id && !pick.staff && <StockCard productId={id} />}
      {/* the seller's own unit cost for Kârlılık: saved on its own, never shown to the warehouse */}
      {id && !pick.staff && <ProductCostCard productId={id} canEdit={!readOnly} />}
      <fieldset disabled={readOnly} className="plain stack">
        <section className="card">
          <div className="card-h">
            <h2>Ürün bilgileri</h2>
          </div>
          <div className="card-b form-grid">
            <div className="field">
              <label htmlFor="p-sku">SKU (stok kodu)</label>
              <input id="p-sku" className="input mono" value={f.sku} onChange={set('sku')} />
              <span className="hint">Siparişlerde ve Excel yüklemede ürünün anahtarı</span>
            </div>
            <div className="field wide">
              <label htmlFor="p-name">Ürün adı</label>
              <input id="p-name" className="input" value={f.name} onChange={set('name')} />
            </div>
            <div className="field">
              <label htmlFor="p-brand">Marka</label>
              <input id="p-brand" className="input" value={f.brand} onChange={set('brand')} />
            </div>
            <div className="field">
              <label htmlFor="p-category">Kategori</label>
              <input id="p-category" className="input" value={f.category} onChange={set('category')} />
            </div>
            <div className="field">
              <label htmlFor="p-image">Görsel adresi</label>
              <input id="p-image" className="input" placeholder="https://" value={f.imageUrl} onChange={set('imageUrl')} />
            </div>
            {id && (
              <div className="field">
                <label htmlFor="p-status">Durum</label>
                <select id="p-status" className="select" value={f.status} onChange={(e) => setForm({ ...f, status: e.target.value as ProductStatus })}>
                  <option value="active">Aktif</option>
                  <option value="inactive">Pasif (yeni siparişe ayrılmaz)</option>
                  <option value="archived">Arşiv</option>
                </select>
              </div>
            )}
          </div>
        </section>

        <section className="card">
          <div className="card-h">
            <h2>Barkodlar</h2>
            <span className="sub">Okutulunca bu ürün bulunur; ana barkod etiketlere basılır</span>
          </div>
          <div className="card-b stack">
            {fresh.length > 0 && (
              <div className="banner ok">
                <Icon name="check" />
                <div style={{ flex: 1, minWidth: 0 }}>
                  {fresh.length === 1 ? 'Yeni barkod kaydedildi' : 'Yeni barkodlar kaydedildi'}: {fresh.map((c) => codeChoiceText(c, f.sku)).join(', ')}. Ürüne ya da koliye
                  yapıştırılacak etiketi şimdi basabilirsiniz.
                </div>
                <div className="row" style={{ gap: 6 }}>
                  {fresh.map((c) => (
                    <button key={c.barcode} type="button" className="btn sm primary" onClick={() => setLabeling({ code: c.barcode })}>
                      <Icon name="printer" size={16} /> {fresh.length === 1 ? 'Barkodu bas' : c.barcode}
                    </button>
                  ))}
                  <button type="button" className="icon-btn" aria-label="Kapat" onClick={() => setFresh([])}>
                    <Icon name="x" size={16} />
                  </button>
                </div>
              </div>
            )}
            {f.barcodes.map((b, i) => (
              <div key={i} className="row">
                <input
                  className="input mono"
                  style={{ maxWidth: 260 }}
                  placeholder="EAN-13"
                  value={b.value}
                  onChange={(e) => setForm({ ...f, barcodes: f.barcodes.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)) })}
                />
                <CodeChip value={b.value} />
                {printable(b.value) && <PrintCode onPrint={printable(b.value)!} />}
                <label className="row" style={{ gap: 6 }}>
                  <input type="radio" name="primary" checked={b.primary} onChange={() => setForm({ ...f, barcodes: f.barcodes.map((x, j) => ({ ...x, primary: j === i })) })} />
                  Ana barkod
                </label>
                {f.barcodes.length > 1 && (
                  <button type="button" className="icon-btn" aria-label="Barkodu sil" onClick={() => setForm({ ...f, barcodes: f.barcodes.filter((_, j) => j !== i) })}>
                    <Icon name="x" size={16} />
                  </button>
                )}
              </div>
            ))}
            {saved &&
              saved.barcodes
                .filter((b) => b.packagingLevel !== 'each' && !saved.packagings.some((k) => k.level === b.packagingLevel))
                .map((b) => (
                  <div key={b.barcode} className="row muted" style={{ fontSize: 13 }}>
                    <span>
                      Mal kabulde tanımlanan koli barkodu: <span className="mono">{b.barcode}</span> · bir okutma {b.unitsPerScan} adet sayar
                    </span>
                    <PrintCode onPrint={() => setLabeling({ code: b.barcode })} />
                  </div>
                ))}
            <div className="row">
              <button type="button" className="btn sm" onClick={() => setForm({ ...f, barcodes: [...f.barcodes, { value: '', primary: false }] })}>
                <Icon name="plus" size={16} /> Barkod ekle
              </button>
              {id && !savedCode && !readOnly && (
                <button type="button" className="btn sm" disabled={busy} onClick={() => void internalBarcode()} title="Barkodsuz gelen ürüne 2 ile başlayan, her okuyucunun okuduğu bir EAN-13 verir">
                  <Icon name="tag" size={16} /> İç barkod üret
                </button>
              )}
            </div>
          </div>
        </section>

        <section className="card">
          <div className="card-h">
            <h2>Ölçüler ve paketleme</h2>
            <span className="sub">{desi ? `Bir adet ${desi} desi` : 'Desi, raf yerleşimi ve kargo fiyatı için ölçüleri girin'}</span>
          </div>
          <div className="card-b stack">
            <div className="form-grid">
              <div className="field">
                <label htmlFor="p-l">En (cm)</label>
                <input id="p-l" className="input" inputMode="decimal" value={f.length} onChange={set('length')} />
              </div>
              <div className="field">
                <label htmlFor="p-w">Boy (cm)</label>
                <input id="p-w" className="input" inputMode="decimal" value={f.width} onChange={set('width')} />
              </div>
              <div className="field">
                <label htmlFor="p-h">Yükseklik (cm)</label>
                <input id="p-h" className="input" inputMode="decimal" value={f.height} onChange={set('height')} />
              </div>
              <div className="field">
                <label htmlFor="p-g">Brüt ağırlık (kg)</label>
                <input id="p-g" className="input" inputMode="decimal" value={f.weight} onChange={set('weight')} />
              </div>
            </div>
            <PackFields label="İç koli" value={f.inner} onChange={(inner) => setForm({ ...f, inner })} onPrint={printable(f.inner.barcode)} />
            <PackFields
              label="Koli"
              value={f.kase}
              onChange={(kase) => setForm({ ...f, kase })}
              onItf={primaryEan ? () => itf14FromGtin13(primaryEan, 1) : undefined}
              onPrint={printable(f.kase.barcode)}
            />
          </div>
        </section>

        <section className="card">
          <div className="card-h">
            <h2>Takip</h2>
          </div>
          <div className="card-b stack">
            <label className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={f.trackLot} onChange={(e) => setForm({ ...f, trackLot: e.target.checked })} />
              Lot takibi: mal kabulde lot numarası sorulur
            </label>
            <label className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={f.trackExpiry} onChange={(e) => setForm({ ...f, trackExpiry: e.target.checked })} />
              Son kullanma tarihi (SKT) takibi: siparişe önce yakın tarihli stok gider (FEFO)
            </label>
            {f.trackExpiry && (
              <div className="field" style={{ maxWidth: 260 }}>
                <label htmlFor="p-shelf">Siparişe çıkarken kalan en az raf ömrü (gün)</label>
                <input id="p-shelf" className="input" inputMode="numeric" value={f.minShelfLifeDays} onChange={set('minShelfLifeDays')} />
              </div>
            )}
            <label className="row" style={{ gap: 8 }}>
              <input type="checkbox" checked={f.isFragile} onChange={(e) => setForm({ ...f, isFragile: e.target.checked })} />
              Kırılabilir: paketlemede uyarı gösterilir
            </label>
            <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <input type="checkbox" checked={f.unitLabels} onChange={(e) => setForm({ ...f, unitLabels: e.target.checked })} style={{ marginTop: 4 }} />
              <span style={{ flex: '1 1 0', minWidth: 0 }}>
                Ürünün üstüne barkod etiketi yapıştırılabilir
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  Barkodu olmayan ürün mal kabulde etiketlenir. İşareti kaldırırsanız etiket yapıştırılmaz: rafa bir ürün kartı konur,
                  paketlemede ürün okutulmadan gözle onaylanır.
                </span>
              </span>
            </label>
            <label className="row" style={{ gap: 8, alignItems: 'flex-start' }}>
              <input
                type="checkbox"
                checked={f.shipsAsIs}
                onChange={(e) => setForm({ ...f, shipsAsIs: e.target.checked, labelBeforePick: e.target.checked && f.labelBeforePick })}
                style={{ marginTop: 4 }}
              />
              <span style={{ flex: '1 1 0', minWidth: 0 }}>
                Paketlemeden gönderilir (kendi kutusunda, çuvalında)
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  Kargo etiketi doğrudan ürünün üstüne yapıştırılır; her adet ayrı koli sayılır. Yalnız bu tür ürünlerden oluşan sipariş
                  toplanınca paketlenmiş sayılır, etiketi paketleme masasının yazıcısından çıkar; ürün masaya uğramadan sevkiyata gider. Başka
                  ürünle birlikte giden sipariş masaya gelir.
                </span>
              </span>
            </label>
            <label className="row" style={{ gap: 8, alignItems: 'flex-start', paddingLeft: 26, opacity: f.shipsAsIs ? 1 : 0.5 }}>
              <input
                type="checkbox"
                checked={f.shipsAsIs && f.labelBeforePick}
                disabled={!f.shipsAsIs}
                onChange={(e) => setForm({ ...f, labelBeforePick: e.target.checked })}
                style={{ marginTop: 4 }}
              />
              <span style={{ flex: '1 1 0', minWidth: 0 }}>
                Kargo etiketi toplamaya çıkmadan basılır
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  Toplama rotası oluşunca etiket masanın yazıcısından çıkar. Toplayıcı etiketi alır, ürünü rafta etiketler ve doğrudan
                  Sevkiyat alanına bırakır (ör. 35 kg çuval: sepete ve masaya gelmez).
                </span>
              </span>
            </label>
          </div>
        </section>
      </fieldset>
      <div className="row">
        {!readOnly && (
          <button className="btn primary" disabled={busy || !f.sku.trim() || !f.name.trim() || (!id && !pick.ready)} onClick={() => void save()}>
            <Icon name="check" /> {id ? 'Kaydet' : 'Ürünü ekle'}
          </button>
        )}
        {!id && pick.staff && !pick.ready && <span className="hint bad">Ürünü hangi satıcı için eklediğinizi seçin</span>}
        {existing.data && (
          <span className="muted" style={{ fontSize: 13 }}>
            Stok: {fmt.n(existing.data.available)} kullanılabilir / {fmt.n(existing.data.physical)} fiziksel · son değişiklik {fmt.dateTime(existing.data.updatedAt)}
          </span>
        )}
      </div>
    </>
  );
}
